import type { ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ActionIcon } from '@mantine/core';
import { Activity, BarChart3, PieChart, Sparkles, TrendingUp, X, type LucideIcon } from 'lucide-react';
import FeatureNavLayout from '@/shared/surfaces/FeatureNavLayout';
import type { FeatureNavGroup, FeatureNavItem } from '@/shared/surfaces/FeatureNavTypes';
import { useSurface } from '@/shared/surfaces/useSurface';
import { usePinnedTilesStore } from '@/shared/surfaces/usePinnedTiles';
import TopicNotFound from '@/shared/surfaces/TopicNotFound';
import { TOKEN_USAGE_CATALOG } from './TokenUsageTopicCatalog';

const VIZ_ICONS: Record<string, LucideIcon> = {
    'ranked-bars': BarChart3,
    'ranked-list': BarChart3,
    donut: PieChart,
    trend: TrendingUp,
};

const TOKENS_BASE = '/token-usage';

const ACTIVITY_ITEM: FeatureNavItem = { path: `${TOKENS_BASE}/activity`, label: 'Activity', icon: Activity };

function tokenNavItems(): FeatureNavItem[] {
    return [
        ...TOKEN_USAGE_CATALOG.map((topic) => ({
            path: `${TOKENS_BASE}/${topic.id}`,
            label: topic.navLabel ?? topic.title,
            icon: topic.icon,
        })),
    ];
}

/**
 * The Token Usage surface shell: a full-height interior sub-nav column — the
 * consumption drill-in screens, any pinned tiles, then the activity feed below a
 * divider — beside its own scrollable content area.
 */
export default function TokenUsageLayout(): ReactElement {
    const surface = useSurface('token-usage');
    const store = usePinnedTilesStore();
    const location = useLocation();
    const navigate = useNavigate();

    if (surface === undefined) return <TopicNotFound />;

    const tokens = tokenNavItems();

    const handleUnpin = (id: string, path: string): void => {
        store.remove(id);
        if (location.pathname === path || location.pathname.startsWith(`${path}/`)) {
            navigate(tokens[0]?.path ?? TOKENS_BASE);
        }
    };

    const pinned: FeatureNavItem[] = store.tilesFor(surface.id).map((tile) => {
        const path = `${TOKENS_BASE}/${tile.id}`;
        return {
            path,
            label: tile.spec.title,
            icon: VIZ_ICONS[tile.spec.viz] ?? Sparkles,
            rightSection: (
                <ActionIcon
                    component="div"
                    size="xs"
                    variant="subtle"
                    color="gray"
                    aria-label={`Remove ${tile.spec.title} from the nav`}
                    onClick={(event) => {
                        event.stopPropagation();
                        handleUnpin(tile.id, path);
                    }}
                >
                    <X size={12} />
                </ActionIcon>
            ),
        };
    });

    const groups: FeatureNavGroup[] = [
        { items: tokens, selectGroupLabel: 'Token Usage' },
        ...(pinned.length > 0 ? [{ header: { icon: Sparkles, label: 'Pinned' }, items: pinned, dividerBefore: true, selectGroupLabel: 'Pinned' }] : []),
        { items: [ACTIVITY_ITEM], dividerBefore: true, selectGroupLabel: 'Activity' },
    ];

    return <FeatureNavLayout title="Token Usage" ariaLabel="Token Usage view" groups={groups} />;
}
